import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { useQuoteState } from './quoteState';
import { FormField } from '../formFields';
import useSubmitQuoteForm from '../hooks/useSubmitQuoteForm';
import useAnalyticsData from '../hooks/useAnalyticsData';
import useFileUpload from '../hooks/useFileUpload';
import { uploadTaxCert, uploadRemovalPhotos } from '../api/shopifyApi';

import { findItemByProductId } from '../utils';
import { numericStringOrNull } from '../../utils/utils';
import { ErrorModal } from '../../modal/ErrorModal';
import { SubmissionProgress } from './SubmissionProgress';

import { FormFieldDefinitions, specialRequestsValues, } from '../formDefinitions';
import { surfacingConfig, turfConfig } from '../constants';

import { GoatAnimationCanvas } from './goatAnimationCanvas';
import { calculateLeadScore } from '../calculations/calculateLeadScore';
// import { QuestionIcon } from '../../utils/icons';


// CAN REMOVE LATER
// import { useConstructLineItems } from '../hooks/useConstructQuote';
// import useInstallationCost from '../hooks/useCalculateInstallation';


// import { GoTrash, GoCheckCircle } from "react-icons/go";
// import { FiUploadCloud } from "react-icons/fi";
// import { AiOutlineFilePdf, AiOutlineFileExcel, AiOutlineFileWord, AiOutlineFileZip, AiOutlineFileImage, AiOutlineFile } from "react-icons/ai";



// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'quoteFormData';

function loadFormData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveFormData(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

// File-upload localStorage (under 'quoteFormUploads') is owned by useFileUpload.




function getItemOptionValue(item, optionKey) {
  if (!item) return null;
  const key = String(optionKey).toLowerCase().replace(/\s+/g, '_');
  const rows = Array.isArray(item.options) ? item.options : [];
  const match = rows.find((o) => {
    const k = String(o?.name ?? o?.key ?? o?.label ?? '').toLowerCase().replace(/\s+/g, '_');
    return k === key;
  });
  return match?.value ?? null;
}



// ---------------------------------------------------------------------------
// Field visibility
// ---------------------------------------------------------------------------

/**
 * Derives the effective field list from the base definitions,
 * toggling hidden/visible types based on current form data + cart items.
 *
 * Rule additions here won't require touching the main component.
 */
function computeVisibleFields(baseFields, formData, items) {
  const hasEngineeredDrawings = items.some(item => 
    item.options?.some((option) =>
      ['Sealed Digital Engineered Drawings', 'Sealed Physical Engineered Drawings', 'Sealed Engineered Drawings'].includes(option.value)
    )
  );
  const validTypes = ["Pour in Place Surfacing", "Engineered Wood Fiber", "Artificial Turf"];
  const hasValidSurfacingType = validTypes.includes(formData.surfacing_type);
  const sameProductTypeCount = items.reduce((acc, item) => {
    acc[item.productType] = (acc[item.productType] || 0) + 1;
    return acc;
  }, {});

  const alreadyHasPIPProduct = items.some((item) => item.productId == surfacingConfig.surfacing_product_id);
  const alreadyHasTurfProduct = items.some((item) => item.productId == turfConfig.turf_product_id);

  const hasSurfacingRequest = formData.surfacing_request === 'Yes' && !alreadyHasPIPProduct && !alreadyHasTurfProduct;
  const hasInstallRequest = formData.quote_installation === 'Yes';
  const showMultiPlaygroundNote = (sameProductTypeCount['Large Play Systems'] || 0) > 1;
  const onlyBordersOrGaGa = items.every(item => 
    ['Playground Borders', 'GaGa Ball Pits'].includes(item.productType)
  );
  const hasTightDeadline = formData.accommodations?.includes("I have a very tight and/or strict deadline");

  // Augment special request options if the cart has multiple playgrounds
  const augmentedSpecialRequests = showMultiPlaygroundNote
    ? [
        ...specialRequestsValues,
        'I added lots of playgrounds to my quote, but I am only looking for one',
      ]
    : specialRequestsValues;

  return baseFields
    .map((field) => {
      // Clone so we don't mutate the original definition
      let f = { ...field };

      switch (f.name) {
        // Show text input when "Other" is selected for org type
        case 'quote_installation':
          f.type = onlyBordersOrGaGa ? 'hidden' : 'radio';
          break;
        case 'specific_buying_for':
          f.type = formData.select_buying_for === 'Other (Please Specify)' ? 'text' : 'hidden';
          f.required = f.type === 'text';
          break;
        // Show file upload when tax exempt
        case 'tax_certificate':
          f.type = formData.tax_exempt === 'Yes' ? 'file' : 'hidden';
          // f.required = formData.tax_exempt === 'Yes';
          f.accept = 'application/pdf,.pdf,image/jpeg,image/jpg,image/png,image/webp';
          f.placeholder = 'PDF or image (JPG, PNG, WebP), max 10MB.';
          break;
        case 'surfacing_request':
          f.type = !alreadyHasPIPProduct && !alreadyHasTurfProduct ? 'radio' : 'hidden';
          break;
        case 'surfacing_type':
          f.type = hasSurfacingRequest ? 'select' : 'hidden';
          break;
        case 'current_surface':
          f.type = hasSurfacingRequest ? 'select' : 'hidden';
          break;
        case 'critical_fall_height':
          f.type = hasSurfacingRequest && formData.surfacing_type ? 'select' : 'hidden';
          break;
        case 'surfacing_square_footage':
          f.type = hasSurfacingRequest && hasValidSurfacingType ? 'number' : 'hidden';
          f.min = 500;
          f.placeholder = 'Square Footage (Minimum 500 sq. ft.)';
          break;
        case 'accommodations':
          f.type = alreadyHasPIPProduct || alreadyHasTurfProduct || hasSurfacingRequest || hasInstallRequest ? 'checkbox' : 'hidden';
          break;
        // Show photo upload when removal is requested
        case 'removal_photos':
          f.type = hasInstallRequest && formData.accommodations?.includes('I need to remove old equipment (pictures required for quoting)') ? 'file' : 'hidden';
          f.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
          f.placeholder = 'Image (JPG, PNG, WebP), max 10MB.';
          break;
        // Surfacing sub-fields — only show when surfacing is requested
        case 'project_deadline':
          f.type = hasTightDeadline ? 'date' : 'hidden';
          break;
        case 'site_dimensions':
          f.type = hasInstallRequest ? 'length_width' : 'hidden';
          break;
        // Show free-text when "Other" building code is selected
        case "building_code": 
          f.type = hasEngineeredDrawings ? "select" : "hidden";
          f.required = hasEngineeredDrawings;
          break;
        case 'specific_building_code':
          f.type = hasEngineeredDrawings && formData.building_code === "Other or I don't know..." ? 'text' : 'hidden';
          f.required = false;
          break;
        case 'budget_select':
          f.type = onlyBordersOrGaGa ? 'hidden' : 'radio';
          break;
        case 'timeframe_select':
          f.type = onlyBordersOrGaGa ? 'hidden' : 'radio';
          break;
        case 'looking_for':
          f.type = onlyBordersOrGaGa ? 'hidden' : 'radio';
          break;
        // Augment special requests with multi-playground note
        case 'special_requests':
          f.options = augmentedSpecialRequests;
          f.type = onlyBordersOrGaGa ? 'hidden' : 'checkbox';
          break;

        default:
          break;
      }

      return f;
    })
    .filter((f) => f.type !== 'hidden');
}

// ---------------------------------------------------------------------------
// Section validation helpers
// ---------------------------------------------------------------------------

function validateSection(fields, formData, taxFile) {
  const sectionErrors = {};

  fields.forEach((field) => {
    const value = formData[field.name];

    if (field.name === 'tax_certificate') {
      const hasSelectedFile = !!taxFile?.file;
      if (formData.tax_exempt === 'Yes' && !hasSelectedFile) {
        sectionErrors[field.name] = 'A tax exemption certificate is required.';
      }
    // } else if (field.name === 'removal_photos') {
    //   if (formData.accommodations?.includes('I need to remove old equipment (pictures required for quoting)') && !removalPhotosFile?.file) {
    //     sectionErrors[field.name] = 'Photos of the equipment to be removed are required.';
    //   }
    } else if (field.required && !value) {
      sectionErrors[field.name] = 'This field is required.';
    }

    if (field.type === 'tel' && value && value.replace(/\D/g, '').length !== 10) {
      sectionErrors[field.name] = 'Please enter a valid 10-digit phone number.';
    }

    if (field.name === 'zip_code' && value && value.replace(/\D/g, '').length < 5) {
      sectionErrors[field.name] = 'Zip code must be at least 5 digits.';
    }
  });

  return sectionErrors;
}

// ---------------------------------------------------------------------------
// Section summary (shown in collapsed header)
// ---------------------------------------------------------------------------

function SectionSummary({ fields, formData }) {
  const summaryFields = fields.filter((f) => f.summary);
  if (summaryFields.length === 0) return null;

  return (
    <span className="card__summary">
      {summaryFields.map((field, idx) => {
        let value = formData[field.name] || '';
        if (field.name === 'select_buying_for' && value === 'Other (Please Specify)') {
          value = formData.specific_buying_for || '';
        }
        if (!value) return null;

        return (
          <span
            key={field.name}
            className={`summary-value ${field.summary_style || ''} ${
              field.summary_comma ? 'summary-comma' : ''
            } ${field.summary_line_break ? 'summary-line-break' : ''}`}
          >
            {value}
          </span>
        );
      })}
    </span>
  );
}



// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const QuoteFormAccordion = () => {
  const { items, totalPrice, expirationDate } = useQuoteState();

  const [formData, setFormData] = useState(loadFormData);

  // File uploads — each handle exposes:
  //   { file, uploaded, isUploading, uploadError, handleSelect, clear, uploadIfNeeded }
  // - handleSelect → wired into <FileInput>
  // - clear        → called from handleChange when sibling form values invalidate the file
  // - uploadIfNeeded → called from useSubmitQuoteForm at submit time
  const taxFile = useFileUpload({
    storageKey: 'taxCert',
    errorMessage: 'Tax certificate upload failed.',
    canUpload: () => formData.tax_exempt === 'Yes' && !!formData.email,
    parseResponse: (d) => ({
      fileGid: d?.fileGid ?? null,
      fileUrl: d?.fileUrl ?? null,
      customerId: d?.customerId ?? null,
      customerGid: d?.customerGid ?? null,
    }),
    uploadFn: (file) => uploadTaxCert({
      file,
      email: formData.email,
      customer: {
        firstName: formData.first_name,
        lastName: formData.last_name,
        phone: formData.phone_number,
        company: formData.company,
        address1: formData.address_1,
        address2: formData.address_2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip_code,
      },
    }),
  });

  const removalPhotosFile = useFileUpload({
    storageKey: 'removalPhotos',
    errorMessage: 'Removal photos upload failed.',
    parseResponse: (d) => {
      const first = d?.file ?? d?.files?.[0] ?? null;
      return {
        fileGid: first?.fileGid ?? null,
        fileUrl: first?.fileUrl ?? null,
      };
    },
    uploadFn: (file) => uploadRemovalPhotos({
      file,
      quoteId: localStorage.getItem('quoteId'),
    }),
  });
  
  const [termsAccepted, setTermsAccepted] = useState(false);

  const formRef = useRef(null);
  const sectionRefs = useRef({});

  // const selectedState = formData.state;
  // const lineItems = useConstructLineItems(items, selectedState);

  const analyticsData = useAnalyticsData();

  // If the quote contains the PIP/Turf "placeholder" product, treat surfacing as requested
  // and back-fill the hidden surfacing form fields from the product's option values.
  useEffect(() => {
    const pipItem = findItemByProductId(items, surfacingConfig.surfacing_product_id);
    const turfItem = findItemByProductId(items, turfConfig.turf_product_id);
    const kind = pipItem ? 'pip' : turfItem ? 'turf' : null;

    if (!kind) return;

    const source = kind === 'pip' ? pipItem : turfItem;
    const nextType = kind === 'pip' ? 'Pour in Place Surfacing' : 'Artificial Turf';

    const optionFallHeight = getItemOptionValue(source, 'critical_fall_height');
    const optionSqft = getItemOptionValue(source, 'square_footage');

    setFormData((prev) => {
      const next = { ...prev };

      next.surfacing_request = 'Yes';
      next.surfacing_type = nextType;

      if (optionFallHeight != null && optionFallHeight !== '') {
        // Normalize values like "4 Feet" -> "4" to match form formatting
        const normalized = numericStringOrNull(optionFallHeight);
        next.critical_fall_height = (normalized ?? String(optionFallHeight)).trim();
      }
      if (optionSqft != null && optionSqft !== '') {
        next.surfacing_square_footage = optionSqft.trim();
      }

      return next;
    });
  }, [items]);



  // Derived: the filtered + visibility-toggled field list
  // const visibleFields = useMemo(
  //   () => computeVisibleFields(FormFieldDefinitions, formData, items),
  //   [formData, items]
  // );

  const visibleFields = useMemo(
    () => computeVisibleFields(FormFieldDefinitions, formData, items),
    [
      items,
      formData.surfacing_type,
      formData.surfacing_request,
      formData.quote_installation,
      formData.accommodations,
      formData.select_buying_for,
      formData.tax_exempt,
      formData.building_code,
    ]
  );

  // Group visible fields by section
  const groupedFields = useMemo(() => {
    return visibleFields
      .filter((f) => f.section)
      .reduce((acc, field) => {
        acc[field.section] = acc[field.section] || [];
        acc[field.section].push(field);
        return acc;
      }, {});
  }, [visibleFields]);

  const sectionKeys = useMemo(() => Object.keys(groupedFields), [groupedFields]);
  const sectionKeysKey = useMemo(() => sectionKeys.join('|'), [sectionKeys]);

  // Accordion state — open the first section on mount
  const [openSections, setOpenSections] = useState(() =>
    sectionKeys.length > 0 ? { [sectionKeys[0]]: true } : {}
  );

  const [sectionErrors, setSectionErrors] = useState({});
  
  
  // Keep accordion state in sync if the available sections change.
  // Ensures at least one section is open and drops any removed sections.
  useEffect(() => {
    if (sectionKeys.length === 0) {
      setOpenSections({});
      return;
    }
    setOpenSections((prev) => {
      const next = {};
      // Preserve currently-open sections that still exist.
      for (const key of sectionKeys) {
        if (prev?.[key]) next[key] = true;
      }
      // If nothing is open (or the previously-open section disappeared), open the first.
      if (Object.keys(next).length === 0) {
        next[sectionKeys[0]] = true;
      }

      // If nothing actually changed, don't force a re-render.
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length) {
        let same = true;
        for (const k of nextKeys) {
          if (!prev?.[k]) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }

      return next;
    });
  }, [sectionKeysKey]);

  const {
    formErrors,
    setFormErrors,
    submitForm,
    installationDetails,
    surfacingDetails,
    baseLineItems,
    submissionLineItems,
    isSubmitting,
    submissionStep,
    showModal,
    setShowModal,
    submitErrors,
  } = useSubmitQuoteForm(items, formData, taxFile, removalPhotosFile, totalPrice, expirationDate, analyticsData);

  // FOR DEBUGGING ONLY
  const baseLineItemsJson = useMemo(() => JSON.stringify(baseLineItems ?? [], undefined, 2), [baseLineItems]);
  


  // Persist formData to localStorage on every change (400ms debounce)
  useEffect(() => {
    // saveFormData(formData); // CLEAR NEXT 2 LINES TO DISABLE DEBOUNCE
    const timer = setTimeout(() => saveFormData(formData), 400);
    return () => clearTimeout(timer);
  }, [formData]);


  // -------------------------------------------------------------------------
  // Form data handlers
  // -------------------------------------------------------------------------

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    // Keep composite site_dimensions in sync with site_length/site_width
    if (name === 'site_length' || name === 'site_width') {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        const length = name === 'site_length' ? value : (prev.site_length || '');
        const width = name === 'site_width' ? value : (prev.site_width || '');
        next.site_dimensions = `${length} x ${width}`.trim();
        return next;
      });
    } else {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };

        // Clear surfacing child fields when surfacing is turned off
        if (name === 'surfacing_request' && value !== 'Yes') {
          delete next.surfacing_type;
          delete next.current_surface;
          delete next.critical_fall_height;
          delete next.surfacing_square_footage;
        }

        // Clear installation child fields when installation is turned off
        if (name === 'quote_installation' && value !== 'Yes') {
          delete next.project_deadline;
          delete next.site_dimensions;
          delete next.site_length;
          delete next.site_width;
        }

        return next;
      });
    }

    // Clear tax cert when tax exempt is turned off
    if (name === 'tax_exempt' && value !== 'Yes') {
      taxFile.clear();
      setFormErrors((prev) => {
        if (!prev.tax_certificate) return prev;
        const next = { ...prev };
        delete next.tax_certificate;
        return next;
      });
    }

    // Clear removal photos when "remove old equipment" is unchecked
    if (name === 'accommodations') {
      const needsRemoval = Array.isArray(value)
        ? value.includes('I need to remove old equipment (pictures required for quoting)')
        : false;
      if (!needsRemoval) {
        removalPhotosFile.clear();
        setFormErrors((prev) => {
          if (!prev.removal_photos) return prev;
          const next = { ...prev };
          delete next.removal_photos;
          return next;
        });
      }
    }

    // Clear error for the field that just changed
    setFormErrors((prev) => {
      if (!prev[name] && !(name === 'site_length' || name === 'site_width')) return prev;
      const next = { ...prev };
      delete next[name];
      if (name === 'site_length' || name === 'site_width') {
        delete next.site_dimensions;
      }
      return next;
    });
  }, [setFormErrors]);

  // (Upload helpers and per-name wrappers now live in useFileUpload.)

  const handleTermsChange = useCallback((e) => {
    const checked = e.target.checked;
    setTermsAccepted(checked);
    setFormData((prev) => ({
      ...prev,
      terms_of_service: checked ? 'I accept the terms of use' : '',
    }));
  }, []);

  // -------------------------------------------------------------------------
  // Accordion navigation
  // -------------------------------------------------------------------------

  // const headerHeight = useMemo(() => {
  //   return document.querySelector('.shopify-section__header')?.offsetHeight ?? 80;
  // }, []);

  const headerHeightRef = useRef(80);
  useEffect(() => {
    headerHeightRef.current = document.querySelector('.shopify-section__header')?.offsetHeight ?? 80;
  }, []);

  const scrollToSection = useCallback((section) => {
    const el = sectionRefs.current[section];
    if (el) {
      window.scrollTo({
        top: el.offsetTop - headerHeightRef.current,
        behavior: 'smooth',
      });
    }
  }, []);

  const toggleSection = useCallback((section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    scrollToSection(section);
  }, [scrollToSection]);

  /**
   * "Next" button: validates current section, then opens + scrolls to next.
   */
  const goToNextSection = useCallback((currentSection) => {
    const fields = groupedFields[currentSection] ?? [];
    const errors = validateSection(fields, formData, taxFile);

    if (Object.keys(errors).length > 0) {
      setFormErrors((prev) => ({ ...prev, ...errors }));
      setSectionErrors((prev) => ({ ...prev, [currentSection]: true }));
      return;
    }

    setSectionErrors((prev) => {
      const next = { ...prev };
      delete next[currentSection];
      return next;
    });

    const currentIdx = sectionKeys.indexOf(currentSection);
    const nextSection = sectionKeys[currentIdx + 1];
    if (nextSection) {
      setOpenSections((prev) => ({ ...prev, [nextSection]: true }));
      scrollToSection(nextSection);
    }
  }, [groupedFields, formData, taxFile, sectionKeys, setFormErrors, scrollToSection]);

  const goToPrevSection = useCallback((currentSection) => {
    const currentIdx = sectionKeys.indexOf(currentSection);
    const prevSection = sectionKeys[currentIdx - 1];
    if (prevSection) {
      setOpenSections((prev) => ({ ...prev, [prevSection]: true }));
      scrollToSection(prevSection);
    }
  }, [sectionKeys, scrollToSection]);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = useCallback(() => {
    submitForm(visibleFields);
  }, [submitForm, visibleFields]);

  const taxCertSelectedOk = formData.tax_exempt !== 'Yes' || !!taxFile?.file;
  const isFormReadyToSubmit = items.length > 0 && termsAccepted && !isSubmitting && taxCertSelectedOk;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (items.length === 0) return null;

  return (
    <>
      <SubmissionProgress step={submissionStep} />
      <div ref={formRef} className="quote-form-wrapper">
        {sectionKeys.map((section, idx) => {
          const fields = groupedFields[section];
          const isOpen = !!openSections[section];
          const hasError = !!sectionErrors[section];
          // const isLast = idx === sectionKeys.length - 1;
          // const isFirst = idx === 0;

          return (
            <div
              key={section}
              className={`accordion-form accordion-form--${section}`}
              ref={(el) => (sectionRefs.current[section] = el)}
            >
              {/* Section header / toggle */}
              <button
                type="button"
                className="card__collapsible-button accordion-toggle"
                aria-expanded={isOpen}
                onClick={() => toggleSection(section)}
              >
                <span className="card__title heading h3">
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </span>

                {hasError && (
                  <span className="card__error-count error-bubble" aria-label="Section has errors">
                    !
                  </span>
                )}

                {!hasError && (
                  <SectionSummary fields={fields} formData={formData} />
                )}

                <span className="plus-button plus-button--large" aria-hidden="true" />
              </button>

              {/* Section content */}
              <div
                id={`section-content-${section}`}
                className={`card__collapsible ${isOpen ? 'expanded' : ''}`}
                style={{ height: isOpen ? 'auto' : 0, overflow: 'hidden' }}
              >
                <div className="grid-container card__collapsible-content">
                  {fields.map((field) => (
                    <React.Fragment key={field.name}>
                      <FormField
                        field={field}
                        value={formData[field.name]}
                        error={formErrors[field.name]}
                        // OptInGroup is the one component that legitimately needs
                        // multi-field access (it reads each sub-field's value);
                        // pass `formData` only in that case so React.memo can
                        // skip re-renders for every other field type when
                        // unrelated values change.
                        formData={(field.type === 'opt_in_group' || field.type === 'length_width') ? formData : undefined}
                        formErrors={field.type === 'length_width' ? formErrors : undefined}
                        handleChange={handleChange}
                        onFileSelect={
                          field.name === 'tax_certificate' ? taxFile.handleSelect :
                          field.name === 'removal_photos' ? removalPhotosFile.handleSelect :
                          undefined
                        }
                      />
                      {/* {field.name === 'quote_installation' &&
                        formData.quote_installation === 'Yes' &&
                        installationDetails?.installationMinimumMet && (
                          <div
                            className="wrapper-quote_installation_minimum_note"
                            style={{
                              flexBasis: '100%',
                              width: '100%',
                              padding: '0 15px',
                              marginTop: '-4px',
                            }}
                          >
                            <span className="quote-tooltip" style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <QuestionIcon tooltip="Based on your current quote, calculated installation is below our standard minimum. Your quote will reflect our minimum installation pricing." />
                            </span>
                          </div>
                        )} */}
                    </React.Fragment>
                  ))}
                </div>

                {/* Prev / Next navigation */}
                <div className="button-nav-container">
                  {(idx > 0 && idx < sectionKeys.length - 1) && (
                    <button
                      type="button"
                      className="button button--small button--outline"
                      onClick={() => goToPrevSection(section)}
                    >
                      Previous
                    </button>
                  )}
                  {idx < sectionKeys.length - 1 && (
                    <button
                      type="button"
                      className="button button--small button--outline"
                      onClick={() => goToNextSection(section)}
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Terms + Submit */}
        <div className="button-container">
          <div className="form-group form__input-wrapper wrapper-terms_of_service">
            <label className="control-label terms-label" htmlFor="terms_of_service">
              By checking the box below and submitting your no-obligation quote, you agree to our&nbsp;
              <a href="/policies/terms-of-service" target="_blank" rel="noopener noreferrer">
                terms of use
              </a>
              .
              <span className="required-asterisk"> *</span>
            </label>
            <input
              type="checkbox"
              name="terms_of_service"
              id="terms_of_service"
              checked={termsAccepted}
              onChange={handleTermsChange}
            />
            <label className="terms-inner-label" htmlFor="terms_of_service">
              I accept the terms of use
            </label>
            {formErrors.terms_of_service && (
              <div className="form-field-error">{formErrors.terms_of_service}</div>
            )}
          </div>

          <button
            type="button"
            id="quote-btn"
            className="button button--primary"
            disabled={!isFormReadyToSubmit}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Submitting…' : 'Submit Quote'}
          </button>

          <a href="#" className="convert_to_cart_button button link link--underline link--underline--offset">
            Convert to Cart
          </a>
        </div>

        <div className="link-container">
          <p className="turn-around-time">
            Current quote turnaround time: <strong>1 business day</strong>
          </p>
        </div>

        {/* Debug payload — only visible for internal email */}
        {['joel+test123@willygoat.com', 'joel@willygoat.com'].includes(formData.email) && (
          
          <div style={{ marginTop: '2rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              Debug: Full submission payload
            </summary>

            <div style={{ marginTop: '0.75rem', fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Debug: Lead Score (live)</div>
              {(() => {
                const result = calculateLeadScore(formData, items, totalPrice);
                return (
                  <>
                    <div><strong>Score</strong>: {result.score} (raw: {result.scoreRaw})</div>
                    <div><strong>Pipeline</strong>: {result.breakdown.pipeline}</div>
                    <pre style={{ fontSize: 12, overflow: 'auto' }}>
                      {JSON.stringify(result.breakdown, null, 2)}
                    </pre>
                  </>
                );
              })()}
            </div>

            <div style={{ marginTop: '0.75rem', fontSize: 12 }}>
              <div><strong>installationDetails</strong>: {JSON.stringify(installationDetails ?? '')}</div>
              <div><strong>totalInstallationCost</strong>: {String(installationDetails?.totalInstallationCost ?? '')}</div>  
              <div><strong>totalInstallationPrice</strong>: {String(installationDetails?.totalInstallationPrice ?? '')}</div>
              <div><strong>installationMinimumMet</strong>: {String(installationDetails?.installationMinimumMet)}</div>
              <div><strong>allItemsHaveInstall</strong>: {String(installationDetails?.allItemsHaveInstall)}</div>
              <div><strong>installVariantGid</strong>: {String(installationDetails?.installVariantGid)}</div>
              <div><strong>installDiscountAmount</strong>: {String(installationDetails?.installDiscountAmount)}</div>
              {surfacingDetails?.totalCost !== null && 
                <>
                  <div><strong>surfacingKind</strong>: {String(surfacingDetails?.kind ?? '')}</div>
                  <div><strong>surfacingCost</strong>: {String(surfacingDetails?.totalCost ?? '')}</div>
                  <div><strong>surfacingPrice</strong>: {String(surfacingDetails?.totalPrice ?? '')}</div>
                  <div><strong>surfacingMaterialVariantId</strong>: {String(surfacingDetails?.lineItems?.[0]?.variantId ?? '')}</div>
                  <div><strong>surfacingLaborVariantId</strong>: {String(surfacingDetails?.lineItems?.[1]?.variantId ?? '')}</div>
                </>
              }
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                Debug: Base quote line items (from `useConstructLineItems`)
              </div>
              <pre style={{ fontSize: 12, overflow: 'auto' }}>{baseLineItemsJson}</pre>
            </div>

            
            <pre style={{ fontSize: 12, overflow: 'auto' }}>{JSON.stringify({
              submitQuoteBody: {
                installationDetails,
                surfacingDetails,
                additional_data: {...formData, ...analyticsData},
                // ...analyticsData,
                // What the quote API expects (DraftOrderLineItemInput[])
                items: submissionLineItems,
                // Original cart items (useful for debugging product types like PIP)
                "ORIGINAL": "___________",
                cart_items: items,
                quoteId: localStorage.getItem('quoteId'),
                expirationDate: expirationDate?.toISOString?.() ?? null,
                taxCertFileGid: taxFile?.uploaded?.fileGid ?? null,
                taxCertFileUrl: taxFile?.uploaded?.fileUrl ?? null,
                removalPhotosFileUrl: removalPhotosFile?.uploaded?.fileUrl ?? null,
                removalPhotosFileGid: removalPhotosFile?.uploaded?.fileGid ?? null,
                
                
                inst_conv_p: installationDetails?.totalInstallationPrice,
                inst_conv_p_adj: installationDetails?.totalInstallationPrice === undefined ? 0 : installationDetails?.totalInstallationPrice * 0.05,
                // pip_prc_adj: pip_prc * 0.01,
                // turf_prc_adj: turf_prc * 0.01,
              },
              // webhookBody: WebhookPayload({
              //   items,
              //   formData,
              //   taxFile,
              //   expirationDate,
              //   converted_total: totalPrice ? (totalPrice / 100) : null,
              //   quoteId: localStorage.getItem('quoteId'),
              //   analyticsData,
              //   totalInstallationCost: installationDetails?.totalInstallationCost || undefined,
              //   totalInstallationPrice: installationDetails?.totalInstallationPrice || undefined,
              // }),
              }, undefined, 2)}
              </pre>

            

              <GoatAnimationCanvas />
            
          </div>
        )}
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={showModal}
        errors={submitErrors}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};
